<template>
  <modal
    v-if="show"
    height="700px"
    width="560px"
    bodyPadding="0"
    :title="form.id ? t('Edit role') : t('Create role')"
    @close="checkClose"
    :read-article-url="`https://docs.bemob.com/${locale}/multi-user-access`"
  >
    <div class="dialog-wrapper">
      <div class="details">
        <div class="top">
          <div class="email align-center" v-if="form.id">
            <ui-icon
              size="16"
              :color="$colors.green"
              icon="fas fa-envelope"
              class="mr-4"
            />
            <div class="email-text">{{ form.login }}</div>
            <div :class="'email-status email-status--' + form.status">
              <ui-icon
                size="12"
                :color="form.status === 'active' ? $colors.green : $colors.red"
                class="mr-4"
                :icon="`fas ${form.status === 'active' ? 'fa-check' : 'fa-lock'}`"
              />
              {{ statusText(form.status) }}
            </div>
          </div>

          <div class="mb-8">
            <ui-radio-group v-model="form.role">
              <template #label>
                <span class="font-weight-bold">{{ t('Role') }}</span>
                <ui-tooltip-icon
                  preset="help"
                  class="mb-2 ml-4"
                  :text="
                    t(
                      'According to the role selected your team member will be assigned with the dedicated permissions. Admin role has a higher priority than a user role.',
                    )
                  "
                />
              </template>
              <div class="mt-8 d-flex">
                <ui-radio
                  value="admin"
                  :label="t('Admin')"
                  planLimits="memberAdminsAmount"
                />
                <ui-radio
                  class="ml-16"
                  value="user"
                  :label="t('User')"
                  planLimits="memberUsersAmount"
                />
              </div>
            </ui-radio-group>
          </div>
          <div class="d-flex">
            <ui-input
              v-if="!form.id"
              v-model="form.login"
              class="ml-8 ml-first-0 flex-grow-1"
              name="login"
              :label="t('Email')"
              :placeholder="t('Email')"
              :error-messages="errorsDict['login']"
              @change="clearError('login')"
            />

            <ui-input
              v-model="form.fullName"
              class="ml-8 ml-first-0 flex-grow-1"
              :label="t('____x.name')"
              :placeholder="t('____x.name')"
              :error-messages="errorsDict['fullName']"
              @change="clearError('fullName')"
            />
          </div>
          <div class="access-options">
            <ui-checkbox
              class="access-option"
              v-model="form.twoFactorEnabled"
              :label="t('Two Factor Authentication')"
              :help-tooltip="
                t(
                  'Two-step authentication allows to protect your account. It is required to specify both password and security code from mobile app to access the panel.',
                )
              "
            />
            <ui-checkbox
              class="access-option"
              v-model="form.accessCustomConversions"
              :label="t('Allow Access to Custom Conversions')"
              :help-tooltip="
                t('Mark the checkbox to allow the access to the Custom Conversions management.')
              "
            />
          </div>
          <div style="display: flex" class="pt-8 pb-8 justify-end" v-if="form.id">
            <ui-button
              :disabled="form.status === 'blocked'"
              :text="t('Log In')"
              icon="fas fa-sign-in"
              @click="showConfirmImpersonate"
            />

            <ui-button
              :text="t('Reset Password')"
              icon="fas fa-key"
              @click="resetPasswordConfirmCall"
            />

            <ui-button
              v-if="isResetTwoFactorAvailable"
              :button-style="form.twoFactorDisableCheck ? 'outline-accent' : 'default'"
              icon="fas fa-mobile-alt"
              :text="t('Reset 2FA')"
              @click="reset2FAButtonHandle"
            />

            <ui-button
              :icon="form.status === 'active' ? 'fas fa-lock' : 'fas fa-lock-open'"
              :text="form.status === 'blocked' ? t('Unblock User') : t('Block User')"
              :button-style="form.status === 'blocked' ? 'outline-alt' : 'default'"
              @click="handleBlockClick"
            />
          </div>
        </div>
        <div class="workspaces">
          <div class="workspaces-title mt-8 mb-8">
            <div>{{ t('Private Workspaces') }}</div>
            <div>
              <ui-button
                :text="t('Assign Workspace')"
                icon="fas fa-plus"
                button-style="outline-accent"
                menu-content-class="checkbox-menu"
                :menu-props="{ location: 'bottom end', offset: [0, 0] }"
                v-model:isMenuOpen="isAddWorkspaceMenuOpened"
              >
                <template #menu>
                  <div class="checkbox-menu__content">
                    <div class="checkbox-menu__top">
                      <ui-checkbox
                        v-model="addMemberMenuIsSelectedAll"
                        class="mt-4 mb-8 checkbox"
                        :label="t('All workspaces')"
                      />
                      <div class="checkbox-menu__search">
                        <ui-input
                          v-model="workspacesSearch"
                          prepend-inner-icon="fas fa-search"
                          :placeholder="t('Search')"
                          :hide-details="1"
                        />
                      </div>
                      <template v-if="searchedWorkspaces.length > 0">
                        <ui-checkbox
                          class="checkbox-menu__list-item checkbox"
                          :true-value="workspace"
                          v-model="addWorkspaceMenuSelected"
                          v-for="workspace in searchedWorkspaces"
                          :key="workspace.id"
                          :label="workspace.name"
                        />
                      </template>
                      <ui-not-found v-else />
                    </div>
                    <div class="checkbox-menu__bottom">
                      <ui-button
                        :text="t('Cancel')"
                        icon="fas fa-times"
                        :disabled="loading"
                        @click="toggleAddWorkspacesMenu"
                      />

                      <ui-button
                        :text="t('Save')"
                        icon="fas fa-check"
                        button-style="accent"
                        :loading="loading"
                        @click="saveWorkspaceMenu"
                      />
                    </div>
                  </div>
                </template>
              </ui-button>
            </div>
          </div>
          <ui-info class="mb-16" :html="helpText" />
          <div class="workspaces-user">
            <ui-table
              class="table-no-border-right custom_pagination workspace-table"
              :show-pagination="false"
              :virtualize="false"
              :use-search="false"
              :loading="loading"
              :headers="headers"
              :items="tableRows"
            >
              <template #item.name="{ item }"> {{ item.name }}</template>
              <template #item.actions="{ item }">
                <button
                  @click="removeWorkspaceConfirmCall(item.id)"
                  type="button"
                  :style="{ color: 'var(--color-red)' }"
                >
                  <ui-icon
                    size="12"
                    :color="$colors.red"
                    icon="fas fa-unlink"
                    class="mr-4"
                    :style="{ 'vertical-align': 'baseline' }"
                  />
                  {{ t('Remove') }}
                </button>
              </template>
            </ui-table>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <section class="justify-space-between align-center xs12 error-wrapper d-flex">
        <error-aggregator v-model="errors" v-model:active="errorsControlActive" />
        <div class="d-flex justify-end align-items-center">
          <ui-update-info infoFormat="short" v-if="form.id" :data="user" />
          <ui-button
            :text="t('Cancel')"
            icon="fas fa-times"
            :disabled="loading"
            @click="checkClose"
          />

          <ui-button
            :text="t('Save')"
            icon="fas fa-check"
            button-style="accent"
            :loading="loading"
            @click="save"
          />
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import _ from 'lodash';
import md5 from 'md5';
import {
  postMemberApiRequest,
  postMemberResetPasswordApiRequest,
  putMemberApiRequest,
} from '@/services/api/user-api';
import { refreshUserProfileFromApi } from '@/services/api/api-services/load-profile';
import { impersonateMember } from '@/services/api/api-services/impersonate-member';
import type { UserWorkspace } from '@/services/models';
import {
  useApiFieldErrors,
  useColors,
  useI18n,
  useMixpanel,
  usePlanLimits,
} from '@/composables';
import { useStore } from '@/store';
import $emitter from '@/services/emitter';

type MemberForm = {
  login: string;
  role: string;
  fullName: string;
  lastName: string;
  status: string;
  twoFactorEnabled: boolean;
  twoFactorDisableCheck: boolean;
  accessCustomConversions: boolean;
  workspaces: UserWorkspace[];
  id?: string;
  password?: string;
  workspaceIds?: string[];
};

const props = defineProps<{
  data?: {
    user?: Record<string, unknown> | null;
    privateWorkspacesData?: UserWorkspace[];
    privateWorkspacesDataNormalized?: Record<string, UserWorkspace>;
    refreshWorkspaces?: () => Promise<void>;
    refreshMembers?: () => Promise<void>;
  };
}>();

const emit = defineEmits<{ close: [] }>();

const { t, locale } = useI18n();
const $colors = useColors();
const store = useStore();
const { trackMixpanel } = useMixpanel();
const { hasPlanLimitsRestriction } = usePlanLimits(undefined, false);
const {
  fieldErrors,
  errors,
  errorsControlActive,
  errorsDict,
  clearErrors,
  applyApiErrors,
} = useApiFieldErrors();

const show = ref(false);
const loading = ref(false);
const isResetTwoFactorAvailable = ref(false);
const user = ref<Record<string, unknown> | null>(null);
const isAddWorkspaceMenuOpened = ref(false);
const addWorkspaceMenuSelected = ref<UserWorkspace[]>([]);
const allPrivateWorkspaces = ref<UserWorkspace[]>([]);
const allPrivateWorkspacesNormalized = ref<Record<string, UserWorkspace>>({});
const workspacesSearch = ref('');
const oldHash = ref('');

const getDefaultRole = () => {
  if (!hasPlanLimitsRestriction('memberAdminsAmount')) {
    return 'admin';
  }
  if (!hasPlanLimitsRestriction('memberUsersAmount')) {
    return 'user';
  }
  return '';
};

const getDefaultForm = (): MemberForm => ({
  login: '',
  role: getDefaultRole(),
  fullName: '',
  lastName: '',
  status: 'active',
  twoFactorEnabled: false,
  twoFactorDisableCheck: false,
  accessCustomConversions: false,
  workspaces: [],
});

const form = reactive<MemberForm>(getDefaultForm());

const getCurrentHash = () => md5(JSON.stringify(form));

const clearError = (field: string) => {
  const newValue = { ...fieldErrors.value };
  delete newValue[field];
  fieldErrors.value = newValue;
};

const helpText = computed(() => {
  if (form.role === 'admin') {
    return t(
      'Admin has full access to Public and all private workspaces assigned to him.',
    );
  }

  if (form.id) {
    return t(
      'User has the access to Public workspace and private workspaces assigned to him.<br> The access to the separate elements should be managed through Permissions setting.',
    );
  }

  return t(
    'Users receive the Full access to all the elements by default.<br> It is possible to change the access type in Permissions settings below.',
  );
});

const tableRows = computed(() => {
  return form.workspaces.map(workspace => ({
    name: workspace.name,
    actions: '',
    id: workspace.id,
  }));
});

const statusText = (status: string) => {
  switch (status) {
    case 'active':
      return t('Active');
    case 'blocked':
      return t('Blocked');
    default:
      return '';
  }
};

const headers = computed(() => [
  {
    title: t('Name'),
    value: 'name',
    sortable: false,
  },
  {
    value: 'actions',
    sortable: false,
  },
]);

const searchedWorkspaces = computed(() => {
  const searchValue = workspacesSearch.value.trim().toLowerCase();
  if (!searchValue) {
    return allPrivateWorkspaces.value;
  }

  return allPrivateWorkspaces.value.filter(workspace => {
    return workspace.name && workspace.name.toLowerCase().includes(searchValue);
  });
});

const addMemberMenuIsSelectedAll = computed({
  get: () => addWorkspaceMenuSelected.value.length === allPrivateWorkspaces.value.length,
  set: (value: boolean) => {
    if (value) {
      addWorkspaceMenuSelected.value = [...allPrivateWorkspaces.value];
      return;
    }
    addWorkspaceMenuSelected.value = [];
  },
});

const open = (member: Record<string, unknown> | null) => {
  loading.value = false;

  if (member && member.id) {
    user.value = member;
    const formValues = _.pick(member, [
      'login',
      'password',
      'fullName',
      'role',
      'id',
      'status',
      'twoFactorEnabled',
      'twoFactorDisableCheck',
      'accessCustomConversions',
      'workspaces',
    ]) as MemberForm;

    formValues.fullName = `${member.firstName || ''} ${member.lastName || ''}`.trim();
    formValues.twoFactorDisableCheck = false;
    formValues.accessCustomConversions = !!member.accessCustomConversions;
    formValues.workspaces = (formValues.workspaces || []) as UserWorkspace[];

    Object.assign(form, formValues);
    isResetTwoFactorAvailable.value = Boolean(formValues.twoFactorEnabled);
    show.value = true;
    store.setLoading(false);
  } else {
    Object.assign(form, getDefaultForm());
    user.value = null;
    show.value = true;
  }

  oldHash.value = getCurrentHash();

  allPrivateWorkspaces.value = [...(props.data?.privateWorkspacesData ?? [])];
  allPrivateWorkspacesNormalized.value = {
    ...(props.data?.privateWorkspacesDataNormalized ?? {}),
  };

  const selected: UserWorkspace[] = [];
  form.workspaces.forEach(workspace => {
    selected.push(allPrivateWorkspacesNormalized.value[workspace.id]);
  });
  addWorkspaceMenuSelected.value = [...selected];
  workspacesSearch.value = '';
};

const createMember = async (payload: Record<string, unknown>) => {
  await postMemberApiRequest(payload);
  await refreshUserProfileFromApi();
  if (props.data?.refreshMembers) {
    await props.data.refreshMembers();
  }
  close();
  $emitter.emit('toast-success', t('User successfully added.'));
};

const updateMember = async (payload: Record<string, unknown>) => {
  await putMemberApiRequest(form.id, payload);
  await refreshUserProfileFromApi();
  if (props.data?.refreshMembers) {
    await props.data.refreshMembers();
  }
  close();
  $emitter.emit('toast-success', t('User successfully changed.'));
};

const save = async () => {
  clearErrors();
  loading.value = true;
  store.setLoading(true);

  let payload: Record<string, unknown> = {
    ...form,
    workspaceIds: form.workspaces.map(workspace => workspace.id),
  };

  payload = _.omit(payload, ['workspaces']);

  if (!form.twoFactorDisableCheck) {
    payload = _.omit(payload, ['twoFactorDisableCheck']);
  }

  try {
    if (form.id) {
      await updateMember(payload);
    } else {
      await createMember(payload);
    }
    if (props.data?.refreshWorkspaces) {
      await props.data.refreshWorkspaces();
    }
  } catch (error: unknown) {
    loading.value = false;
    applyApiErrors(error, { nested: true });
    return Promise.reject(error);
  } finally {
    store.setLoading(false);
  }
};

const saveWorkspaceMenu = () => {
  form.workspaces = [...addWorkspaceMenuSelected.value];
  toggleAddWorkspacesMenu();
};

const checkClose = () => {
  if (oldHash.value !== getCurrentHash()) {
    $emitter.emit('open-dialog', {
      name: 'ConfirmDialog',
      data: {
        onConfirm: () => close(),
      },
    });
    return;
  }
  close();
};

const close = () => {
  emit('close');
};

const resetPasswordConfirmCall = () => {
  $emitter.emit('open-dialog', {
    name: 'ConfirmDialog',
    data: {
      message: t('The password will be reset if you press OK.'),
      onConfirm: () => {
        void postMemberResetPasswordApiRequest(form.id).then(() =>
          $emitter.emit('toast-success', t('Password successfully reset.')),
        );
      },
    },
  });
};

const showConfirmImpersonate = () => {
  $emitter.emit('open-dialog', {
    name: 'ConfirmDialog',
    data: {
      message: t(
        'When logging in as another user or admin, you will be provided with the read-only access to settings of this member.<br> No editing in the member’s account allowed.<br> You can go back to your account by clicking <b>Return back</b> at the top of the page.',
      ),
      confirmButtonText: t('Log In As Member'),
      confirmButtonIcon: 'fas fa-sign-in',
      onConfirm: () => {
        impersonateMember({
          id: form.id,
          email: form.login,
        });

        trackMixpanel('settings_impersonate', {
          source: 'modal',
          owner_email: store.user.email,
          member_email: form.login,
        });
      },
    },
  });
};

const removeWorkspaceConfirmCall = (id: string) => {
  $emitter.emit('open-dialog', {
    name: 'ConfirmDialog',
    data: {
      message: t('All selected items will be deleted permanently if you press OK.'),
      onConfirm: () => {
        form.workspaces = form.workspaces.filter(workspace => workspace.id !== id);
      },
    },
  });
};

const reset2FAButtonHandle = () => {
  if (form.twoFactorDisableCheck) {
    form.twoFactorDisableCheck = false;
    return;
  }

  $emitter.emit('open-dialog', {
    name: 'ConfirmDialog',
    data: {
      message: t(
        'The current 2FA settings will be reset for this role if you press OK. When logging in to the panel it will be required to scan the QR code again and specify the code from the app.',
      ),
      onConfirm: () => {
        form.twoFactorDisableCheck = true;
      },
    },
  });
};

const handleBlockClick = () => {
  form.status = form.status === 'blocked' ? 'active' : 'blocked';
};

const toggleAddWorkspacesMenu = () => {
  isAddWorkspaceMenuOpened.value = !isAddWorkspaceMenuOpened.value;
};

watch(isAddWorkspaceMenuOpened, opened => {
  if (!opened) return;

  const selected: UserWorkspace[] = [];
  form.workspaces.forEach(workspace => {
    selected.push(allPrivateWorkspacesNormalized.value[workspace.id]);
  });
  addWorkspaceMenuSelected.value = [...selected];
  workspacesSearch.value = '';
});

onMounted(() => {
  open(props.data?.user ?? null);
});
</script>

<style lang="scss" scoped>
@import '@/styles/dialogs/report-modals';

.dialog-wrapper {
  overflow: hidden;

  .details {
    max-height: none;
    overflow: visible;
  }
}

.access-options {
  display: flex;
  align-items: flex-start;
  width: 100%;
}

.access-option {
  flex: 0 0 50%;
  width: 50% !important;
  max-width: 50%;
  min-width: 0;
  box-sizing: border-box;
}

.checkbox {
  display: flex !important;
}

.error-wrapper {
  width: 100%;
}

.status-button {
  &--blocked {
    border: 1px solid var(--color-red) !important;
  }
}

.email {
  font-size: 16px;
  font-weight: bold;
  display: flex;
}

.email-text {
  max-width: 76%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.email-status {
  font-size: 12px;
  font-weight: normal;
  border-radius: 4px;
  display: flex;
  margin-left: 4px;
  align-items: center;
  padding: 0 8px;
  height: 24px;
  text-transform: capitalize;
  &--active {
    background: var(--color-green10);
  }
  &--blocked {
    background: var(--color-pink);
  }
}

.main {
  flex-direction: column;
}

.wrap {
  padding: 0 !important;
}

.top {
  padding: 16px 16px 0 16px;
  background: var(--color-gray5);
}

.workspaces {
  background: var(--color-white);
  padding: 0 16px 16px 16px;
}

.workspaces-title {
  font-weight: bold;
  font-size: 16px;
  line-height: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  .v-btn {
    margin-top: 8px;
    margin-right: 0;
    border: 1px solid var(--color-green);
  }
}

.workspace-table {
  :deep(.table-wrapper) {
    max-height: 220px;
    min-height: 0;
  }

  :deep(.ui-table__inner) {
    table-layout: fixed;
  }

  :deep(.table-header > .table-cell:last-child),
  :deep(.table-body .table-row > .table-cell:last-child) {
    width: 90px;
    text-align: right;
  }

  :deep(.table-header > .table-cell:first-child),
  :deep(.table-body .table-row > .table-cell:first-child) {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
}

.dialog-title-text {
  text-transform: capitalize;
}
</style>
